/**
 * LeiDry: comportamento del carrello (cassetto e pagina /cart).
 *
 * 1. Regalo automatico. La pochette (settings.ldy_regalo_prodotto) entra da sola quando l'importo
 *    che conta per lo sconto Shopify «Pochette da viaggio in regalo» raggiunge
 *    settings.ldy_soglia_regalo_cent ed esce quando si scende sotto. Il conto (uguale a quello di
 *    Shopify: i capi già usati dal 2+1 non contano) lo fa snippets/ldy-premi.liquid sul carrello vero
 *    e lo scrive in [data-ldy-premi]; qui si esegue solo l'azione indicata, con questi paletti:
 *    - si lavora dopo OGNI cambio del carrello (eventi standard) e all'apertura della pagina, quando
 *      il carrello di Horizon è pronto ad ascoltare (sennò il ridisegno andrebbe perso);
 *    - una correzione alla volta: i cambi che arrivano intanto si mettono in fila, mai due aggiunte;
 *    - prima di correggere si aspetta che parta la modifica di quantità appena toccata (Horizon la
 *      ritarda di 300 ms) e, durante la correzione, le righe non si toccano: Horizon le cambia per
 *      posizione e la pochette che entra o esce sposta le posizioni;
 *    - al massimo 3 correzioni per ogni gesto della cliente, così non può nascere un giro infinito;
 *    - se la pochette nel carrello non risulta gratis (manca lo sconto automatico del titolare) o
 *      Shopify la rifiuta, si toglie e non si rimette più per tutta la sessione; la barra smette di
 *      prometterla (attributo data-ldy-regalo-bloccato su <html>, letto da ldy-cassetto-stili);
 *    - aggiungerla non apre il cassetto se era chiuso.
 * 2. <ldy-consigliati-component>: «Aggiungi» con un tocco, una richiesta, il cassetto resta aperto.
 * 3. Bottone della cassa: un tocco solo; se il carrello si sta ancora aggiornando aspetta e poi parte.
 * 4. Conto alla rovescia della fascia «carrello riservato» (snippets/ldy-cassetto-riservato.liquid),
 *    che esiste solo sul tema in anteprima: parte da 10:00 e la scadenza resta in sessionStorage.
 *    Sul tema pubblicato la fascia non c'è e qui non parte niente.
 * 5. Piede del cassetto fisso in basso anche dopo il primo prodotto aggiunto (vedi avviaPiedeFisso).
 *
 * Il ridisegno resta quello di Horizon: ogni richiesta porta le sezioni del carrello e
 * CartItemsComponent le applica dal CartLinesUpdateEvent.
 */
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import {
  TEMPO_MASSIMO_MS,
  aggiungiAlCarrello,
  cambiaRiga,
  rileggiCarrello,
  segnaOccupato,
  sezioniCarrello,
  statoPremi,
} from '@theme/ldy-carrello';

const FONTE_REGALO = 'ldy-regalo';
const FONTE_CONSIGLIATI = 'ldy-consigliati';
const CHIAVE_BLOCCO = 'ldy-regalo-non-gratis';
const ATTRIBUTO_BLOCCO = 'data-ldy-regalo-bloccato';
const MAX_CORREZIONI = 3;
/** Horizon manda la modifica di quantità 300 ms dopo l'ultimo tocco: si aspetta un po' di più. */
const ATTESA_DOPO_TOCCO_MS = 400;
/** Tempo massimo per aspettare che il carrello di Horizon sia pronto all'apertura della pagina. */
const ATTESA_AVVIO_MS = 4000;
/** Dopo l'invio verso la cassa il bottone si libera da solo se la pagina non è cambiata. */
const SBLOCCO_CASSA_MS = 8000;
const PROPRIETA_REGALO = { _ldy_regalo: 'pochette' };

/** @typedef {NonNullable<ReturnType<typeof statoPremi>>} StatoPremi */

/** Copia in memoria del blocco, per quando sessionStorage non si può usare. */
let bloccoInMemoria = false;

function regaloBloccato() {
  if (bloccoInMemoria) return true;
  try {
    return window.sessionStorage.getItem(CHIAVE_BLOCCO) === '1';
  } catch {
    return false;
  }
}

/** Mostra o nasconde la promessa della pochette nella barra (fuori dal ridisegno, su <html>). */
function segnaBlocco() {
  document.documentElement.toggleAttribute(ATTRIBUTO_BLOCCO, regaloBloccato());
}

function bloccaRegalo() {
  bloccoInMemoria = true;
  try {
    window.sessionStorage.setItem(CHIAVE_BLOCCO, '1');
  } catch {
    /* resta il blocco in memoria per questa pagina, più il limite di 3 correzioni */
  }
  segnaBlocco();
}

/** @returns {Promise<void>[]} le modifiche di quantità di Horizon ancora in corso */
function aggiornamentiInCorso() {
  const CartItems = /** @type {any} */ (customElements.get('cart-items-component'));
  const pendenti = CartItems?.pendingQuantityUpdates;
  return Array.isArray(pendenti) ? pendenti : [];
}

/**
 * Ferma i tocchi sulle righe del carrello mentre la pochette entra o esce. La classe è quella che
 * Horizon usa per lo stesso scopo; il ridisegno la toglie da solo quando arrivano le righe nuove.
 * @param {boolean} acceso
 */
function fermaRighe(acceso) {
  for (const modulo of document.querySelectorAll('form.cart-form')) {
    modulo.classList.toggle('cart-items-disabled', acceso);
  }
}

/**
 * Stato dei regali preso dall'HTML delle sezioni di una risposta del carrello.
 * @param {unknown} sections
 * @returns {StatoPremi | undefined} undefined quando la risposta non porta sezioni del carrello
 */
function statoDaSezioni(sections) {
  if (!sections || typeof sections !== 'object') return undefined;
  let carrelloVisto = false;
  for (const html of Object.values(sections)) {
    if (typeof html !== 'string' || !html.includes('cart-items-component')) continue;
    carrelloVisto = true;
    if (!html.includes('data-ldy-premi')) continue;
    // DOMParser e non una ricerca nel testo: il <template> del carrello vuoto contiene una copia
    // dei dati che querySelector, giustamente, non vede.
    const stato = statoPremi(new DOMParser().parseFromString(html, 'text/html'));
    if (stato) return stato;
  }
  // Carrello in pagina ma senza dati (per esempio vuoto nella pagina /cart): niente da fare.
  return carrelloVisto ? { importo: 0, azione: 'niente', chiave: '', regaloKey: '', regaloId: 0 } : undefined;
}

/**
 * Esegue fn senza far aprire il cassetto (se era chiuso): l'arrivo della pochette non è un gesto
 * della cliente. cart-drawer-component decide l'apertura nel momento in cui parte l'evento.
 * @template T
 * @param {() => T} fn
 * @returns {T}
 */
function senzaAutoApertura(fn) {
  const componente = document.querySelector('cart-drawer-component[auto-open]');
  const drawer = /** @type {any} */ (document.querySelector('theme-drawer#cart-drawer'));
  const aperto = Boolean(drawer?.isOpen ?? drawer?.hasAttribute('open'));
  if (!componente || aperto) return fn();
  componente.removeAttribute('auto-open');
  try {
    return fn();
  } finally {
    componente.setAttribute('auto-open', '');
  }
}

/** @param {number} ms */
function pausa(ms) {
  return new Promise((ok) => setTimeout(ok, ms));
}

class RegaloAutomatico {
  #inCorso = false;
  #correzioni = 0;
  /** Ultimo evento partito e ultimo applicato: le risposte vecchie arrivate tardi si ignorano. */
  #partiti = 0;
  #applicato = 0;
  /** @type {StatoPremi | null} */
  #ultimo = null;
  /** @type {Array<() => void>} */
  #attese = [];
  /** Momento dell'ultimo tocco su un selettore di quantità (performance.now()). */
  #ultimoTocco = -Infinity;

  avvia() {
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#suCambio);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#suCambio);
    document.addEventListener(ThemeEvents.cartSectionRestored, this.#suRipristino);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#suTocco);
    segnaBlocco();

    // Questo modulo può girare prima di component-cart-items.js (viene prima nella pagina): un
    // evento lanciato adesso non lo sentirebbe nessuno e il cassetto non si ridisegnerebbe.
    Promise.race([customElements.whenDefined('cart-items-component'), pausa(ATTESA_AVVIO_MS)]).then(() => {
      if (!this.#applicato) this.#ultimo = statoPremi(document);
      this.sincronizza();
    });
  }

  /** Vero mentre una correzione è in corso. */
  get occupato() {
    return this.#inCorso;
  }

  /** @returns {number} millisecondi da aspettare perché parta la quantità appena toccata */
  attesaTocco() {
    return Math.max(0, this.#ultimoTocco + ATTESA_DOPO_TOCCO_MS - performance.now());
  }

  /** @returns {Promise<void>} si risolve quando non c'è più niente da correggere */
  attendi() {
    if (!this.#inCorso) return Promise.resolve();
    return new Promise((ok) => this.#attese.push(ok));
  }

  #suTocco = () => {
    this.#ultimoTocco = performance.now();
  };

  /** @param {Event} event */
  #suCambio = (event) => {
    const promessa = /** @type {any} */ (event).promise;
    if (!promessa || typeof promessa.then !== 'function') return;
    const numero = ++this.#partiti;

    promessa
      .then(async (/** @type {any} */ risultato) => {
        const detail = risultato?.detail;
        if (detail?.didError) return;
        if (detail?.source !== FONTE_REGALO) this.#correzioni = 0;

        let stato = statoDaSezioni(detail?.sections);
        if (stato === undefined) stato = await this.#statoDiRiserva();
        if (!stato || numero < this.#applicato) return;

        this.#applicato = numero;
        this.#ultimo = stato;
        this.sincronizza();
      })
      .catch(() => {
        /* richiesta fallita: il carrello non è cambiato */
      });
  };

  #suRipristino = () => {
    this.#correzioni = 0;
    this.#ultimo = statoPremi(document);
    this.sincronizza();
  };

  /** Risposta senza sezioni (aggiornamento da un'app esterna): si chiede la sezione una volta. */
  async #statoDiRiserva() {
    const id = sezioniCarrello()[0];
    if (!id) return null;
    try {
      const html = await sectionRenderer.getSectionHTML(id, false);
      return statoDaSezioni({ [id]: html }) ?? null;
    } catch {
      return null;
    }
  }

  async sincronizza() {
    if (this.#inCorso) return;
    this.#inCorso = true;
    try {
      for (;;) {
        // Le quantità che la cliente sta cambiando adesso hanno la precedenza.
        const attesa = this.attesaTocco();
        if (attesa > 0) {
          await pausa(attesa);
          continue;
        }
        const pendenti = aggiornamentiInCorso();
        if (pendenti.length) {
          await Promise.allSettled(pendenti);
          continue;
        }

        const stato = this.#ultimo;
        if (!stato || !this.#daFare(stato)) break;
        if (this.#correzioni >= MAX_CORREZIONI) break;
        this.#correzioni += 1;

        // #esegui manda il suo CartLinesUpdateEvent: #suCambio aggiorna #ultimo prima che si torni qui.
        this.#ultimo = null;
        const riuscito = await this.#esegui(stato);
        if (!riuscito) break;
      }
    } finally {
      this.#inCorso = false;
      for (const ok of this.#attese.splice(0)) ok();
    }
  }

  /** @param {StatoPremi} stato */
  #daFare(stato) {
    switch (stato.azione) {
      case 'aggiungi':
        return stato.regaloId > 0 && !regaloBloccato();
      case 'togli':
      case 'uno':
      case 'togli-non-gratis':
        return Boolean(stato.chiave);
      default:
        return false;
    }
  }

  /** @param {StatoPremi} stato */
  async #esegui(stato) {
    fermaRighe(true);
    try {
      if (stato.azione === 'aggiungi') {
        await senzaAutoApertura(() =>
          aggiungiAlCarrello(document.body, [{ id: stato.regaloId, quantity: 1, properties: PROPRIETA_REGALO }], {
            context: 'cart',
            fonte: FONTE_REGALO,
          })
        );
      } else {
        if (stato.azione === 'togli-non-gratis') bloccaRegalo();
        await cambiaRiga(document.body, stato.chiave, stato.azione === 'uno' ? 1 : 0, { fonte: FONTE_REGALO });
      }
      return true;
    } catch (errore) {
      const nome = /** @type {Error} */ (errore)?.name;
      if (nome === 'LdyCartError' && stato.azione === 'aggiungi') {
        // Shopify non la accetta (esaurita, non in vendita sul negozio online…): non si riprova.
        bloccaRegalo();
      } else if (nome === 'AbortError') {
        // Tempo scaduto: la richiesta può essere arrivata lo stesso. Si ridisegna dal carrello vero.
        await rileggiCarrello();
        this.#ultimo = statoPremi(document);
      }
      if (nome !== 'AbortError') console.warn('[ldy-cassetto] regalo:', errore);
      return false;
    } finally {
      fermaRighe(false);
    }
  }
}

const regalo = new RegaloAutomatico();

/**
 * Bottone della cassa (name="checkout", classe ldy-cassa): un tocco solo. Se una quantità o il
 * regalo si stanno ancora aggiornando, aspetta (al massimo 12 secondi) e poi invia il modulo.
 * Non si disattiva il bottone: un bottone disattivato non invierebbe il modulo.
 */
function proteggiCassa() {
  /** @param {HTMLElement} bottone */
  const libera = (bottone) => {
    delete bottone.dataset.ldyInvio;
    bottone.removeAttribute('aria-busy');
  };

  /** @param {HTMLElement} bottone */
  const liberaPiuTardi = (bottone) => {
    setTimeout(() => libera(bottone), SBLOCCO_CASSA_MS);
  };

  document.addEventListener(
    'click',
    (event) => {
      const bottone = event.target instanceof Element ? event.target.closest('button.ldy-cassa[name="checkout"]') : null;
      if (!(bottone instanceof HTMLButtonElement) || bottone.disabled) return;

      if (bottone.dataset.ldyInvio) {
        event.preventDefault();
        return;
      }
      bottone.dataset.ldyInvio = '1';
      bottone.setAttribute('aria-busy', 'true');

      const tocco = regalo.attesaTocco();
      if (!tocco && !aggiornamentiInCorso().length && !regalo.occupato) {
        liberaPiuTardi(bottone);
        return; // parte subito, invio normale del modulo
      }

      event.preventDefault();
      const pronto = (async () => {
        if (tocco) await pausa(tocco);
        await Promise.allSettled(aggiornamentiInCorso());
        await regalo.attendi();
      })();
      Promise.race([pronto, pausa(TEMPO_MASSIMO_MS)]).then(() => {
        // Il ridisegno può aver sostituito il bottone: si usa quello presente adesso.
        const attuale = document.querySelector('button.ldy-cassa[name="checkout"]');
        const scelto = attuale instanceof HTMLButtonElement ? attuale : bottone;
        if (scelto.disabled || !scelto.form) {
          libera(bottone);
          return;
        }
        scelto.dataset.ldyInvio = '1';
        scelto.setAttribute('aria-busy', 'true');
        liberaPiuTardi(scelto);
        scelto.form.requestSubmit(scelto);
      });
    },
    true
  );

  // Tornando indietro dalla cassa la pagina può uscire dalla cache del browser: bottone di nuovo libero.
  window.addEventListener('pageshow', () => {
    for (const bottone of document.querySelectorAll('button.ldy-cassa[data-ldy-invio]')) {
      if (bottone instanceof HTMLElement) libera(bottone);
    }
  });
}

/**
 * Prodotti consigliati del carrello (snippets/ldy-consigliati.liquid).
 * @extends {Component}
 */
class LdyConsigliatiComponent extends Component {
  /** @param {SubmitEvent} event */
  async aggiungi(event) {
    event.preventDefault();
    const modulo = event.target;
    if (!(modulo instanceof HTMLFormElement) || modulo.dataset.ldyInCorso) return;

    const id = Number(new FormData(modulo).get('id'));
    if (!id) return;

    const bottone = modulo.querySelector('button[type="submit"]');
    const errore = modulo.querySelector('[data-ldy-errore]');
    modulo.dataset.ldyInCorso = '1';
    bottone?.setAttribute('aria-busy', 'true');
    if (errore instanceof HTMLElement) {
      errore.hidden = true;
      errore.textContent = '';
    }
    segnaOccupato(true);

    try {
      await aggiungiAlCarrello(this, [{ id, quantity: 1 }], { context: 'cart', fonte: FONTE_CONSIGLIATI });
      // La scheda sparisce col ridisegno (il prodotto ora è nel carrello): il fuoco non resta nel vuoto.
      requestAnimationFrame(() => {
        if (document.activeElement && document.activeElement !== document.body) return;
        const titolo =
          document.getElementById('cart-drawer-heading') ?? document.querySelector('.cart-page .cart-title h1');
        if (!(titolo instanceof HTMLElement)) return;
        titolo.setAttribute('tabindex', '-1');
        titolo.focus({ preventScroll: true });
      });
    } catch (motivo) {
      const nome = /** @type {Error} */ (motivo)?.name;
      // Tempo scaduto: il prodotto può essere entrato lo stesso, si ridisegna dal carrello vero.
      if (nome === 'AbortError') await rileggiCarrello();
      if (errore instanceof HTMLElement && errore.isConnected) {
        const testo = nome === 'LdyCartError' ? /** @type {Error} */ (motivo).message : '';
        errore.textContent = testo || this.dataset.errore || '';
        errore.hidden = false;
      }
    } finally {
      delete modulo.dataset.ldyInCorso;
      bottone?.removeAttribute('aria-busy');
      segnaOccupato(false);
    }
  }

  /**
   * Cambio di variante nella tendina: aggiorna il prezzo della scheda.
   * @param {Event} event
   */
  cambiaVariante(event) {
    const tendina = event.target;
    if (!(tendina instanceof HTMLSelectElement)) return;
    const opzione = tendina.selectedOptions[0];
    const modulo = tendina.form;
    if (!opzione || !modulo) return;

    const prezzo = modulo.querySelector('[data-ldy-prezzo]');
    if (prezzo) prezzo.textContent = opzione.dataset.prezzo ?? '';
  }
}

const CHIAVE_RISERVATO = 'ldy-riservato-fine';
const DURATA_RISERVATO_MS = 10 * 60 * 1000;
/** Scadenza tenuta in memoria quando sessionStorage non si può usare. */
let fineRiservatoInMemoria = 0;

/** @returns {number} il momento (ms) in cui il conto alla rovescia arriva a 00:00 */
function fineRiservato() {
  try {
    let fine = Number(window.sessionStorage.getItem(CHIAVE_RISERVATO));
    if (!fine) {
      fine = Date.now() + DURATA_RISERVATO_MS;
      window.sessionStorage.setItem(CHIAVE_RISERVATO, String(fine));
    }
    return fine;
  } catch {
    if (!fineRiservatoInMemoria) fineRiservatoInMemoria = Date.now() + DURATA_RISERVATO_MS;
    return fineRiservatoInMemoria;
  }
}

/** Scrive il tempo che resta in ogni [data-ldy-timer]; false se nella pagina non ce n'è nessuno. */
function aggiornaRiservato() {
  const tempi = document.querySelectorAll('[data-ldy-timer]');
  if (!tempi.length) return false;
  const resto = Math.max(0, fineRiservato() - Date.now());
  const minuti = Math.floor(resto / 60000);
  const secondi = Math.floor((resto % 60000) / 1000);
  const testo = `${String(minuti).padStart(2, '0')}:${String(secondi).padStart(2, '0')}`;
  for (const tempo of tempi) {
    if (tempo.textContent !== testo) tempo.textContent = testo;
  }
  return true;
}

/**
 * Fa scorrere il conto alla rovescia. Parte solo quando la fascia c'è (anteprima); il ridisegno del
 * carrello rimette «10:00» dal Liquid, quindi si corregge subito, appena cambia il cassetto.
 */
function avviaRiservato() {
  let acceso = false;
  const accendi = () => {
    if (acceso || !aggiornaRiservato()) return;
    acceso = true;
    setInterval(aggiornaRiservato, 1000);
    const osservatore = new MutationObserver(() => aggiornaRiservato());
    for (const radice of document.querySelectorAll('#cart-drawer, .cart-page')) {
      osservatore.observe(radice, { childList: true, subtree: true, characterData: true });
    }
  };
  accendi();
  // Carrello vuoto all'apertura: la fascia arriva col primo ridisegno.
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const promessa = /** @type {any} */ (event).promise;
    if (promessa && typeof promessa.then === 'function') {
      promessa.then(() => requestAnimationFrame(accendi), () => {});
    }
  });
}

/** Stessa soglia di assets/cart-drawer.js: oltre metà dell'altezza del cassetto il piede non è fisso. */
const SOGLIA_PIEDE = 0.5;

/**
 * Tiene il piede del cassetto (Totale e «Checkout sicuro») fisso in basso.
 * Horizon decide «cart-summary-sticky» quando il carrello cambia, ma al primo prodotto (cassetto
 * vuoto che si riempie) le righe arrivano dopo, dentro una view transition: la misura trova il piede
 * ancora assente, scrive "false" e il bottone della cassa resta in fondo alla lista finché il
 * cassetto non si riapre. Qui si rifà la stessa misura quando il piede compare o cambia misura.
 */
function avviaPiedeFisso() {
  const dialogo = document.querySelector('#cart-drawer dialog');
  if (!dialogo || typeof ResizeObserver === 'undefined') return;

  const misura = () => {
    const piede = dialogo.querySelector('.cart-drawer__summary');
    const altezza = dialogo.getBoundingClientRect().height;
    // Cassetto chiuso o vuoto: decide Horizon (e rimisura lui all'apertura).
    if (!piede || !dialogo.open || !altezza) return;
    const valore = piede.getBoundingClientRect().height / altezza > SOGLIA_PIEDE ? 'false' : 'true';
    if (dialogo.getAttribute('cart-summary-sticky') !== valore) {
      dialogo.setAttribute('cart-summary-sticky', valore);
    }
  };

  /** @type {Element | null} */
  let piedeOsservato = null;
  const osservatore = new ResizeObserver(misura);
  osservatore.observe(dialogo);
  const aggancia = () => {
    const piede = dialogo.querySelector('.cart-drawer__summary');
    if (piede === piedeOsservato) return;
    if (piedeOsservato) osservatore.unobserve(piedeOsservato);
    piedeOsservato = piede;
    if (piede) osservatore.observe(piede);
    misura();
  };
  new MutationObserver(aggancia).observe(dialogo, { childList: true, subtree: true });
  aggancia();
}

if (!customElements.get('ldy-consigliati-component')) {
  customElements.define('ldy-consigliati-component', LdyConsigliatiComponent);
}

// Il modulo si carica sia dal cassetto sia dalla pagina /cart: si avvia una volta sola.
const globale = /** @type {any} */ (window);
if (!globale.__ldyCassetto) {
  globale.__ldyCassetto = true;
  regalo.avvia();
  proteggiCassa();
  avviaRiservato();
  avviaPiedeFisso();
}
